/**
 * Sample prompts for CSTCopilot
 * These are used for bot suggestions, prompt suggestions, and follow-up question rotation
 *
 * Prompt distribution:
 *   - tenantBotSuggestions: First 5 basic prompts per tenant → welcome chips
 *   - tenantPromptsSuggestions: 6 prompts (mix of basic + medium) → prompts gallery modal
 *   - tenantAllPrompts: All prompts (basic + medium) per tenant → follow-up rotation
 */

import type { PromptSection, PromptSuggestion } from './promptData';

// Tenant IDs for NL2SQL-supported tenants
export const TENANT_IDS = {
    ALL_REQUESTS: 'f15bec0f-2b85-47d7-8939-6565b09ebd89',
    AMM: '628691d2-59ef-4452-9f02-1d837497cc9a',
    PARTNER_INVESTMENTS: '4b9ac222-75ab-4566-a4e4-c9553896f290',
    AZURE_AI_CAPACITY: '3d6753f0-f836-4e55-8323-049767b02179',
    AZURE_SPONSORSHIP_ISV: 'af315f0d-0028-49ed-b197-1bbf897d56c8',
    COMPETE: '46cd8037-9f33-43c8-9262-432efd3317c6',
    COSELL: 'e4f33c50-d2ff-4b1c-8b1b-21b8ed18bc3d',
    GSI: 'd5a5c2d1-b8be-4b18-a4c3-6d09e5f0c59c',
    MACC: '6f76ed48-1b55-4951-920a-90b995e4d5dd',
    MARKETPLACE: 'f9e0f41d-707c-46af-b24b-626dc68601ce',
    MSECR: 'd6d337f6-2ff5-4d52-9a74-5d9dad09164b',
    MCAPS_HELP_PM: '1762e166-b734-43de-a37d-63aadd76becc',
    MCAPS_LEADER_ROB: '6f76ed48-1b55-4951-920a-90b995e4d5cc',
    MDC_ATTACH_PURPLE_EXCEPTION: '1e3033d1-ae10-4d59-99b9-b7a6dd6e30d4',
    NON_AI_CAPACITY: '2b6b4852-8108-4aef-afca-d951b9fade7c',
    RED_BUTTON: 'b878ed1d-5d4c-4a64-9716-221f9484f381',
    REGIONAL_SKILLING: '7e4d3c2b-1a9f-4e8d-9c6b-5a4f3e2d1c0b',
    SGS: '5461fe99-0356-49d5-be95-63cbbe8a187a',
    TECH_ROB: 'fdd1b0d7-b70a-4e73-b4a3-de6ac7ba3f5b',
} as const;

type TenantId = (typeof TENANT_IDS)[keyof typeof TENANT_IDS];

/**
 * Tenant-specific bot suggestion prompts for NL2SQL-supported tenants
 * First 5 basic prompts shown in the bot welcome message for each tenant
 */
export const tenantBotSuggestions: Record<TenantId, string[]> = {
    [TENANT_IDS.AMM]: [
        'Show AMM Offer Desk requests by state',
        'Show the monthly trend of AMM Offer Desk requests',
        'Show in-progress AMM Offer Desk requests',
        'Top 10 requests by credit amount requested',
        'Show AMM requests by offer',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.PARTNER_INVESTMENTS]: [
        'Show Partner Investments requests by state',
        'Show Partner Investments request trend by month',
        'Top 10 partners by investment amount',
        'Show in-progress Partner Investments requests',
        'Show Partner Investments requests by stage',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.ALL_REQUESTS]: [
        'How do I submit a new action in UAT?',
        'What is Unified Action Tracker (UAT)?',
        'Show actions created this week',        
        'Show action trends by month',
        'Show 10 in-progress actions sorted by creation date',
        'Show actions associated with the Walgreen account',
        
    ],
    [TENANT_IDS.AZURE_AI_CAPACITY]: [
        'Top requested Azure OpenAI (AOAI) models',
        'Identify blocked AOAI requests with the highest revenue impact.',
        'Show AOAI requests that are currently on hold.',
        'Show 3P GPU requests',
        'Show GBB\u2011validated AOAI requests.',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.AZURE_SPONSORSHIP_ISV]: [
        'Show Azure Sponsorship for ISV requests by state',
        'Show the monthly trend of Azure Sponsorship ISV requests',
        'Top 10 partner solutions by investment amount',
        'Show in-progress Azure Sponsorship ISV requests',
        'Show Azure Sponsorship ISV requests expiring this quarter',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.COMPETE]: [
        'What is our win rate against Google',
        'who are the top competitors in compete actions',
        'Show Compete action distribution over the products',
        'For compete, Show monthly win rate trend — are we improving over time?',
        'Show compete actions where opportunity outcome is Won',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.COSELL]: [
        'Show the distribution of CoSell actions by area',
        'List the top 10 partners based on the number of Cosell actions',
        'List all of the Cosell deals that have been won during the previous quarter',
        'Which partners have the highest number of expired deals?',
        'How many CoSell requests were completed last quarter?',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.GSI]: [
        'Show GSI ACO PIR requests by state',
        'Show GSI ACO PIR request trend by month',
        'Top 10 partners by GSI credit amount',
        'Show in-progress GSI ACO PIR requests',
        'Show GSI ACO PIR requests by deal structure',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.MACC]: [
        'Show count of MACC actions by State',
        'Show the monthly trend of MACC actions',
        'Show MACC actions in progress',
        'Show count of MACC actions by account',
        'Show the average days to close MACC actions',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.MCAPS_HELP_PM]: [
        'Show count of MCAPS Help PM actions by State',
        'Show the monthly trend of MCAPS Help PM actions',
        'Show MCAPS Help PM actions in progress',
        'Show count of MCAPS Help PM actions by account',
        'Average number of days to close MCAPS Help PM actions',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.MARKETPLACE]: [
        'Show Microsoft Marketplace actions by state',
        'Show Microsoft Marketplace action trend by month',
        'Show in-progress Microsoft Marketplace actions',
        'Top 10 partners by Microsoft Marketplace action count',
        'Show Microsoft Marketplace actions by request type',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.MSECR]: [
        'Show Microsoft Security Research requests by state',
        'Show Microsoft Security Research request trend by month',
        'Show in-progress Microsoft Security Research requests',
        'Show Microsoft Security Research requests by request type',
        'Show Microsoft Security Research requests by workload',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.MCAPS_LEADER_ROB]: [
        'Show MCAPS Leader RoB actions by state',
        'Show MCAPS Leader RoB action trend by month',
        'Show in-progress MCAPS Leader RoB actions',
        'Show MCAPS Leader RoB actions by priority',
        'Show MCAPS Leader RoB actions by assigned team',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.MDC_ATTACH_PURPLE_EXCEPTION]: [
        'Show MDC Attach Purple Exception requests by state',
        'Show MDC Attach exception trend by month',
        'Show in-progress MDC Attach Purple Exception requests',
        'Top 10 accounts by Defender for Cloud exception count',
        'Show MDC Attach exceptions by reason',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.NON_AI_CAPACITY]: [
        'Show Non-AI capacity requests by state',
        'Show the trend of non-AI capacity requests by month',
        'Show Non-AI capacity requests by Azure preferred region',
        'Show a breakdown of hosting types for non-AI capacity requests',
        'Show Non-AI requests by managing owner',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.RED_BUTTON]: [
        'Show Red Button actions by state',
        'Show the trend of the number of red button actions by month',
        'Show Red Button actions created this week',
        'Show Red Button actions by segment',
        'Show all Red Button actions in progress',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.REGIONAL_SKILLING]: [
        'Show Regional Skilling requests by state',
        'Show Regional Skilling request trend by month',
        'Show Regional Skilling requests by campaign',
        'Show in-progress Regional Skilling requests',
        'Show Regional Skilling requests by partner region',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.SGS]: [
        'Show SGS actions by state',
        'Show SGS action trend by month',
        'Show SGS actions by SGS area',
        'Show in-progress SGS actions',
        'Top 10 SGS account owners by action count',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.TECH_ROB]: [
        'What percentage of milestones are blocked vs on track in tech rob?',
        'Show blocked actions by blocker type with average days blocked',
        'Which assigned tech rob teams have the longest average resolution time?',
        'Show P0/P1 actions by tech rob assigned team with days open — who needs help',
        'Month-over-month blocked milestone trend — is the tech blocker count improving?',
        'How do I submit a new action in UAT?',
    ],
};

/**
 * Tenant-specific prompts gallery suggestions for NL2SQL-supported tenants
 * 6 prompts shown in the prompts modal/gallery for each tenant (mix of basic + medium)
 */
export const tenantPromptsSuggestions: Record<TenantId, string[]> = {
    [TENANT_IDS.AMM]: [
        'Show AMM Offer Desk requests by state',
        'Show total ACR by offer',
        'Show exception requests by decision',
        'Show AMM requests by offer desk region',
        'Show high-value credit requests over 100000',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.PARTNER_INVESTMENTS]: [
        'Show Partner Investments requests by stage',
        'Show total investment amount by partner',
        'Show Partner Investments requests by PIR decision',
        'Show high-value investments over 500000',
        'Show Partner Investments requests by request type',
        'How do I submit a new action in UAT?',
    ],
    [TENANT_IDS.ALL_REQUESTS]: [
        'Show my actions',
        'Show actions assigned to me',
        'Show actions requested by me',
        'Show action count by tenant and state',
        'Which tenant has the most in-progress actions?',
        'Show cross-tenant trend by month — actions created per tenant',
    ],
    [TENANT_IDS.AZURE_AI_CAPACITY]: [
        'Show AOAI requests by state',
        'Show AOAI request trend by month',
        'Show approved AOAI requests by model and region',
        'How many AOAI requests were approved vs denied this quarter?',
        'Show AOAI request volume trend by request type over last 6 months',
        'Which AOAI customers have the most blocked actions',
    ],
    [TENANT_IDS.AZURE_SPONSORSHIP_ISV]: [
        'Show Azure Sponsorship ISV requests by state and priority',
        'Top 10 partner solutions by total investment amount',
        'Show sponsorships expiring in the next 90 days',
        'Show ISV sponsorships by use case',
        'Average days from request to sponsorship start',
        'Show monthly trend of approved vs pending ISV sponsorships',
    ],
    [TENANT_IDS.COMPETE]: [
        'Show compete actions by state',
        'Show compete action trend by month',
        'Show my compete actions',
        'Show compete actions by competitor and state',
        'Which accounts have both won and lost compete deals?',
        'Show compete action volume by solution area and outcome',
    ],
    [TENANT_IDS.COSELL]: [
        'On average, how many CoSell actions are created per week this quarter?',
        'What is the average number of days from creation to close for CoSell actions?',
        'Show CoSell actions by deal status',
        'Show co-sell deals by deal status and solution area',
        'Won co-sell deals grouped by segment',
        'Which partners have the most expired deals?',
    ],
    [TENANT_IDS.GSI]: [
        'Show GSI ACO PIR requests by state and priority',
        'Top 10 partners by total credit amount',
        'Show GSI requests by partner type and deal structure',
        'Show GSI request trend by month',
        'Average days from request to credit start date',
        'Show GSI requests by request reason',
    ],
    [TENANT_IDS.MACC]: [
        'Show MACC actions created in the last 7 days',
        'Show my MACC actions',
        'Show MACC actions by state and priority',
        'Top 10 accounts with most MACC actions',
        'Show MACC trend by month — created vs closed',
        'Average days to close MACC actions by priority',
    ],
    [TENANT_IDS.MCAPS_HELP_PM]: [
        'Show MCAPS Help PM actions created this week',
        'Show MCAPS Help actions by priority and state',
        'Top 10 accounts with most MCAPS Help tickets',
        'Show monthly trend of created vs closed MCAPS Help actions',
        'Average days to close by priority',
        'Show MCAPS Help actions by assigned team and state',
    ],
    [TENANT_IDS.MARKETPLACE]: [
        'Show Microsoft Marketplace actions by state and priority',
        'Top 10 partners by Microsoft Marketplace action count',
        'Show Microsoft Marketplace action trend by month',
        'Average days to close Microsoft Marketplace actions by priority',
        'Show Microsoft Marketplace actions by request type',
        'Show in-progress Microsoft Marketplace actions by assigned team',
    ],
    [TENANT_IDS.MSECR]: [
        'Show Microsoft Security Research requests by state and request type',
        'Show Microsoft Security Research requests created in the last 30 days',
        'Show Microsoft Security Research requests by commercial context',
        'Show Microsoft Security Research requests with observed gaps',
        'Show Microsoft Security Research requests by applicable workload',
        'Show Microsoft Security Research requests with resolutions provided',
    ],
    [TENANT_IDS.MCAPS_LEADER_ROB]: [
        'Show MCAPS Leader RoB actions by state and priority',
        'Show MCAPS Leader RoB actions created this quarter',
        'Top 10 accounts with most MCAPS Leader RoB actions',
        'Show MCAPS Leader RoB action trend by month',
        'Average days to close MCAPS Leader RoB actions by priority',
        'Show MCAPS Leader RoB actions by assigned team and state',
    ],
    [TENANT_IDS.MDC_ATTACH_PURPLE_EXCEPTION]: [
        'Show MDC Attach Purple Exception requests by state and priority',
        'Show MDC Attach exceptions by reason for exception',
        'Top 10 accounts with most Defender for Cloud exceptions',
        'Show MDC Attach exception trend by month',
        'Average days to close MDC Attach exceptions by priority',
        'Show in-progress MDC Attach exceptions by exception approver',
    ],
    [TENANT_IDS.NON_AI_CAPACITY]: [
        'show the oldest High-priority Non-AI capacity requests still in progress',
        'Show Non-AI requests by hosting type and state',
        'Requests by Azure preferred region and priority',
        'Show Non-AI request volume by fiscal quarter and state',
        'Which managing owners have the most in-progress requests?',
        'Show average days open by priority level',
    ],
    [TENANT_IDS.RED_BUTTON]: [
        'List actions with Infra location On-Prem in UK & Ireland area',
        'Show Red Button actions by priority and state',
        'Top 10 accounts with most Red Button escalations',
        'Show Red Button trend by month and priority',
        'Average days to close by priority — P0 vs P1 vs P2',
        'Show Red Button actions by segment and state',
    ],
    [TENANT_IDS.REGIONAL_SKILLING]: [
        'Show Regional Skilling requests by state and priority',
        'Show Regional Skilling requests by campaign and sub-campaign',
        'Top 10 partners by Regional Skilling request count',
        'Show Regional Skilling requests by partner region',
        'Show Regional Skilling request trend by month',
        'Show Regional Skilling requests by skilling plan status',
    ],
    [TENANT_IDS.SGS]: [
        'Show SGS actions by state and SGS segment',
        'Show SGS actions by SGS area',
        'Top 10 SGS accounts by action count',
        'Show SGS action trend by month',
        'Show SGS actions by primary growth shaping contact',
        'Average days to close SGS actions by priority',
    ],
    [TENANT_IDS.TECH_ROB]: [
        'Which milestone workloads have the most blocked items this quarter?',
        'Show my Tech RoB actions',
        'Show blocked actions by blocker type and priority',
        'Actions by milestone workload where customer commitment is Required',
        'Show action count by assigned team and state',
        'Show average days open for in-progress actions by assigned team',
    ],
};

/**
 * Complete prompt pool per tenant (basic + medium complexity)
 * Used for follow-up suggestion rotation — 3 prompts rotate after each response
 * When user switches tenants, rotation resets and draws only from the selected tenant
 */
export const tenantAllPrompts: Record<TenantId, string[]> = {
    [TENANT_IDS.AMM]: [
        // Basic
        'Show AMM Offer Desk requests by state',
        'Show the monthly trend of AMM Offer Desk requests',
        'Show in-progress AMM Offer Desk requests',
        'Top 10 requests by credit amount requested',
        'Show AMM requests by offer',
        'Show AMM requests by offer desk region',
        'Show exception requests by decision',
        'Show AMM requests pending my action',
        'Show AMM requests by pre or post sales',
        'Average days to close AMM Offer Desk requests',
        // Medium
        'Show total ACR by offer',
        'Show total ACR by offer desk region',
        'Show high-value credit requests over 100000',
        'Show count of AMM requests by offer and state',
        'Top 10 accounts by total ACR',
        'Show exception requests awaiting decision',
        'Show AMM requests by primary solution play',
        'Show AMM requests created this quarter grouped by offer',
    ],
    [TENANT_IDS.PARTNER_INVESTMENTS]: [
        // Basic
        'Show Partner Investments requests by state',
        'Show Partner Investments request trend by month',
        'Top 10 partners by investment amount',
        'Show in-progress Partner Investments requests',
        'Show Partner Investments requests by stage',
        'Show Partner Investments requests by request type',
        'Show Partner Investments requests by PIR decision',
        'Show investment amount by stage',
        'Show expected incremental revenue by partner',
        'Average days to close Partner Investments requests',
        // Medium
        'Show total investment amount by partner',
        'Show total investment amount by stage',
        'Show high-value investments over 500000',
        'Show count of requests by request type and stage',
        'Top 10 partners by expected incremental revenue',
        'Show Partner Investments requests pending strategy approval',
        'Show requests pending finance or operations approval',
        'Show Partner Investments requests created this quarter by partner',
    ],
    // ── UATGeneral (Cross-Tenant) ──────────────────────────────────────
    [TENANT_IDS.ALL_REQUESTS]: [
        // Basic
        'Show Actions created this week',       
        'Show action trends by month',
        'Show 10 in-progress actions sorted by creation date',
        'Show actions associated with the Walgreen account',
        'Show my actions',
        'Show actions assigned to me',
        'Show actions requested by me',
        // Medium
        'Show high-priority actions',
        'What are the core capabilities of UAT?',
        'What is a Field Request?'

       
    ],

    // ── AOAI (Azure OpenAI Capacity) ───────────────────────────────────
    [TENANT_IDS.AZURE_AI_CAPACITY]: [
        // Basic
        'Top requested Azure OpenAI (AOAI) models',
        'Identify blocked AOAI requests with the highest revenue impact.',
        'Show AOAI requests that are currently on hold.',
        'Show 3P GPU requests',
        'Show GBB\u2011validated AOAI requests.',
        'Show AOAI requests by state',
        'Show AOAI request trend by month',
        // Medium
        'Show approved AOAI requests by model and region',
        'How many AOAI requests were approved vs denied this quarter?',
        'Which accounts have requested more than 50 GPUs?',
        'Show AOAI request volume trend by request type over last 6 months',
        'Requests where region flexibility is yes but decision is still pending',
        'Show average days from creation to decision for approved requests',
        'Top 5 models by total GPUs requested',
        'Show AOAI requests by deal type and decision status',
        'Which pending requests have been waiting for decision longer than 14 days?',
        'Group AOAI models by the highest demand',
        'Which AOAI customers have the most blocked actions',
        'Show 3P GPU request trend by month',
        'Which AOAI customers have been on hold the longest',
        'What are the top blocked reasons for Azure AI Capacity',
        'GPT models with the highest demand in AOAI requests',
        'Show AOAI customers with the most lost opportunities',
        'Which AOAI segments are the most blocked',
    ],
    // ── Azure Sponsorship for ISV ─────────────────────────────
    [TENANT_IDS.AZURE_SPONSORSHIP_ISV]: [
        // Basic
        'Show Azure Sponsorship for ISV requests by state',
        'Show the monthly trend of Azure Sponsorship ISV requests',
        'Top 10 partner solutions by investment amount',
        'Show in-progress Azure Sponsorship ISV requests',
        'Show Azure Sponsorship ISV requests expiring this quarter',
        'Show ISV sponsorships by use case',
        'Show ISV sponsorships by budget program',
        'Show my Azure Sponsorship ISV requests',
        // Medium
        'Show Azure Sponsorship ISV requests by state and priority',
        'Top 10 partner solutions by total investment amount',
        'Show sponsorships expiring in the next 90 days',
        'Average days from request to sponsorship start',
        'Show monthly trend of approved vs pending ISV sponsorships',
        'Show ISV sponsorships by licensing mechanism',
        'Show ISV sponsorships where MarketplaceTransaction is Yes',
        'Show ISV sponsorships by customer/partner location',
        'Total investment amount by partner solution',
        'ISV sponsorships past due date grouped by priority',
    ],
    // ── Compete ────────────────────────────────────────────────────────
    [TENANT_IDS.COMPETE]: [
        // Basic
        'What is our win rate against Google',
        'who are the top competitors in compete actions',
        'Show Compete action distribution over the products',
        'For compete, Show monthly win rate trend — are we improving over time?',
        'Show compete actions where opportunity outcome is Won',
        'Show compete actions by solution area',
        'Compete deals created this quarter grouped by competitor',
        'Average deal value for won vs lost compete actions',
        'show recently created compete actions',
        'Lost deals by competitor and industry — where are we weakest?',
        'Show in-progress compete actions that have been open the longest',
        'Won compete deals by industry vertical',
        'Show win rate by competitor — Google vs Slack vs Zoom',
        'Which competitors have the most actions?',
        'show cosell referrals that are expiring this week',
        'Show compete actions by state',
        'Show compete action trend by month',
        'Show compete actions by country',
        'Show compete actions where opportunity outcome is Won grouped by segment',
        'Show my compete actions',
        // Medium
        'Show compete actions by competitor and state',
        'Which accounts have both won and lost compete deals?',
        'Show compete action volume by solution area and outcome',
        'Show average days to close won vs lost deals',
        'Active compete deals by segment — Enterprise vs SMB',
        'Show compete actions by country and competitor',
        'Which competitors have the most in-progress deals right now?',
    ],

    // ── CoSell ─────────────────────────────────────────────────────────
    [TENANT_IDS.COSELL]: [
        // Basic
        'Show the distribution of CoSell actions by area',
        'List the top 10 partners based on the number of Cosell actions',
        'List all of the Cosell deals that have been won during the previous quarter',
        'Which partners have the highest number of expired deals?',
        'How many CoSell requests were completed last quarter?',
        'On average, how many CoSell actions are created per week this quarter?',
        'What is the average number of days from creation to close for CoSell actions?',
        'Show CoSell actions by deal status',
        // Medium
        'Show co-sell deals by deal status and solution area',
        'Won co-sell deals grouped by segment',
        'Which partners have the most expired deals?',
        'Show co-sell actions by Microsoft owner and deal status',
        'Average days to close won vs expired co-sell deals',
        'Show co-sell referrals accepted this quarter by partner',
        'Co-sell actions by state and priority',
        'Top accounts by number of won co-sell deals',
        'Show monthly trend of accepted vs expired deals',
        'Co-sell actions where due date is past and state is still In Progress',
    ],
    // ── GSI ACO PIR ──────────────────────────────────────────
    [TENANT_IDS.GSI]: [
        // Basic
        'Show GSI ACO PIR requests by state',
        'Show GSI ACO PIR request trend by month',
        'Top 10 partners by GSI credit amount',
        'Show in-progress GSI ACO PIR requests',
        'Show GSI ACO PIR requests by deal structure',
        'Show GSI ACO PIR requests by partner type',
        'Show GSI ACO PIR requests by request reason',
        'Show my GSI ACO PIR requests',
        // Medium
        'Show GSI ACO PIR requests by state and priority',
        'Top 10 partners by total credit amount',
        'Show GSI requests by partner type and deal structure',
        'Average days from request to credit start date',
        'Show GSI requests by licensing program',
        'Show GSI requests by area and solution area',
        'GSI requests where opportunity size amount > 1,000,000',
        'Show GSI requests by commercial executive',
        'GSI requests past due date grouped by priority',
        'Show monthly trend of credit amounts approved',
    ],
    // ── MACC ───────────────────────────────────────────────────────────
    [TENANT_IDS.MACC]: [
        // Basic
        'Show count of MACC actions by State',
        'Show the monthly trend of MACC actions',
        'Show MACC actions in progress',
        'Show count of MACC actions by account',
        'Show the average days to close MACC actions',
        'Show MACC actions created in the last 7 days',
        'Show my MACC actions',
        // Medium
        'Show MACC actions by state and priority',
        'Top 10 accounts with most MACC actions',
        'Show MACC trend by month — created vs closed',
        'Average days to close MACC actions by priority',
        'Show blocked MACC milestones by account',
        'MACC actions by segment and state',
        'Which action owners have the most in-progress MACC items?',
        'Show MACC actions created this quarter by state',
        'MACC actions past due date grouped by priority',
        'Show MACC actions by industry',
    ],

    // ── MCAPSHelpPM ────────────────────────────────────────────────────
    [TENANT_IDS.MCAPS_HELP_PM]: [
        // Basic
        'Show count of MCAPS Help PM actions by State',
        'Show the monthly trend of MCAPS Help PM actions',
        'Show MCAPS Help PM actions in progress',
        'Show count of MCAPS Help PM actions by account',
        'Average number of days to close MCAPS Help PM actions',
        'Show MCAPS Help PM actions created this week',
        // Medium
        'Show MCAPS Help actions by priority and state',
        'Top 10 accounts with most MCAPS Help tickets',
        'Show monthly trend of created vs closed MCAPS Help actions',
        'Average days to close by priority',
        'Show MCAPS Help actions by assigned team and state',
        'Which requestors submitted the most MCAPS Help actions?',
        'MCAPS Help actions past due date grouped by state',
        'Show action owner workload distribution',
        'In-progress MCAPS Help actions by industry',
        'Show MCAPS Help actions by segment and priority',
    ],
    // ── Microsoft Marketplace ──────────────────────────────────────────
    [TENANT_IDS.MARKETPLACE]: [
        // Basic
        'Show Microsoft Marketplace actions by state',
        'Show Microsoft Marketplace action trend by month',
        'Show in-progress Microsoft Marketplace actions',
        'Top 10 partners by Microsoft Marketplace action count',
        'Show Microsoft Marketplace actions by request type',
        'Show my Microsoft Marketplace actions',
        'Show Microsoft Marketplace actions created this week',
        'Show Microsoft Marketplace actions by priority',
        // Medium
        'Show Microsoft Marketplace actions by state and priority',
        'Top 10 accounts by Microsoft Marketplace action count',
        'Average days to close Microsoft Marketplace actions by priority',
        'Show Microsoft Marketplace actions past due date grouped by priority',
        'Show monthly trend of created vs closed Microsoft Marketplace actions',
        'Show Microsoft Marketplace actions by offer type',
        'Show Microsoft Marketplace actions by assigned team and state',
        'Which partners have the most in-progress Microsoft Marketplace actions?',
        'Show Microsoft Marketplace actions by referral status',
        'Show Microsoft Marketplace actions by segment and state',
    ],
    // ── Microsoft Security Research ────────────────────────────────────
    [TENANT_IDS.MSECR]: [
        // Basic
        'Show Microsoft Security Research requests by state',
        'Show Microsoft Security Research request trend by month',
        'Show in-progress Microsoft Security Research requests',
        'Show Microsoft Security Research requests by request type',
        'Show Microsoft Security Research requests by commercial context',
        'Show my Microsoft Security Research requests',
        'Show Microsoft Security Research requests created in the last 30 days',
        'Show Microsoft Security Research requests by applicable workload',
        // Medium
        'Show Microsoft Security Research requests by state and request type',
        'Show Microsoft Security Research requests with observed gaps',
        'Show Microsoft Security Research requests with resolutions provided',
        'Show Microsoft Security Research requests with new coverage implemented',
        'Show MDE requests with analyzer output attached',
        'Show MDI requests without logs attached',
        'Show MDO requests with logs attached',
        'Show Sentinel requests without logs attached',
        'Count Microsoft Security Research requests by commercial context',
        'Show Microsoft Security Research evaluation test windows starting this month',
    ],
    // ── MCAPS Leader RoB ───────────────────────────────────────
    [TENANT_IDS.MCAPS_LEADER_ROB]: [
        // Basic
        'Show MCAPS Leader RoB actions by state',
        'Show MCAPS Leader RoB action trend by month',
        'Show in-progress MCAPS Leader RoB actions',
        'Show MCAPS Leader RoB actions by priority',
        'Show MCAPS Leader RoB actions by assigned team',
        'Show MCAPS Leader RoB actions created this week',
        'Show my MCAPS Leader RoB actions',
        'Show MCAPS Leader RoB actions by segment',
        // Medium
        'Show MCAPS Leader RoB actions by state and priority',
        'Top 10 accounts with most MCAPS Leader RoB actions',
        'Average days to close MCAPS Leader RoB actions by priority',
        'Show MCAPS Leader RoB actions by industry',
        'MCAPS Leader RoB actions past due date grouped by state',
        'Show MCAPS Leader RoB action volume by area and solution area',
        'Show monthly trend of created vs closed MCAPS Leader RoB actions',
        'Show MCAPS Leader RoB actions by opportunity outcome',
        'In-progress MCAPS Leader RoB actions by assigned team',
    ],

    // ── MDC Attach Purple Exception (Defender for Cloud) ─────────────
    [TENANT_IDS.MDC_ATTACH_PURPLE_EXCEPTION]: [
        // Basic
        'Show MDC Attach Purple Exception requests by state',
        'Show MDC Attach exception trend by month',
        'Show in-progress MDC Attach Purple Exception requests',
        'Top 10 accounts by Defender for Cloud exception count',
        'Show MDC Attach exceptions by reason',
        'Show MDC Attach exceptions by priority',
        'Show my MDC Attach Purple Exception requests',
        'Show MDC Attach exceptions where IsSalesEngaged is Yes',
        // Medium
        'Show MDC Attach exceptions by state and priority',
        'Show MDC Attach exceptions by reason for exception',
        'Average days to close MDC Attach exceptions by priority',
        'Show in-progress MDC Attach exceptions by exception approver',
        'MDC Attach exceptions past due date grouped by priority',
        'Show monthly trend of approved vs pending MDC Attach exceptions',
        'Show MDC Attach exceptions by operating unit',
        'Top 10 exception approvers by exception count',
        'Show MDC Attach exceptions by opportunity revenue size',
        'Show MDC Attach exceptions by area support lead',
    ],
    // ── Non-AI Capacity ────────────────────────────────────────────────
    [TENANT_IDS.NON_AI_CAPACITY]: [
        // Basic
        'Show Non-AI capacity requests by state',
        'Show the trend of non-AI capacity requests by month',
        'Show Non-AI capacity requests by Azure preferred region',
        'Show a breakdown of hosting types for non-AI capacity requests',
        'Show Non-AI requests by managing owner',
        'show the oldest High-priority Non-AI capacity requests still in progress',
        // Medium
        'Show Non-AI requests by hosting type and state',
        'Requests by Azure preferred region and priority',
        'Show Non-AI request volume by fiscal quarter and state',
        'Which managing owners have the most in-progress requests?',
        'Show average days open by priority level',
        'Non-AI requests by milestone workload and milestone status',
        'Top 10 accounts with blocked Non-AI requests',
        'Show hosting type trend by month — last 6 months',
        'Requests by accountable exec and state',
        'Non-AI requests past due date grouped by priority',
        'Accounts with the most P0 and P1 Non AI actions still open',
        'Show active Non AI actions where the primary competitor is AWS',
        'Which competitors appear most in blocked Non AI actions?',
        'Show Non AI actions by segment',
        'Non AI actions grouped by blocker category',
    ],

    // ── Red Button ─────────────────────────────────────────────────────
    [TENANT_IDS.RED_BUTTON]: [
        // Basic
        'Show Red Button actions by state',
        'Show the trend of the number of red button actions by month',
        'Show Red Button actions created this week',
        'Show Red Button actions by segment',
        'Show all Red Button actions in progress',
        'List actions with Infra location On-Prem in UK & Ireland area',
        // Medium
        'Show Red Button actions by priority and state',
        'Top 10 accounts with most Red Button escalations',
        'Show Red Button trend by month and priority',
        'Average days to close by priority — P0 vs P1 vs P2',
        'Show Red Button actions by segment and state',
        'Which actions are P0 and have been open longer than 7 days?',
        'Show blocked Red Button actions by account',
        'Red Button actions created vs closed by month — last 6 months',
        'Show action owner workload — actions per owner by state',
        'Red Button actions by industry vertical',
    ],

    // ── Regional Skilling ──────────────────────────────────────────────
    [TENANT_IDS.REGIONAL_SKILLING]: [
        // Basic
        'Show Regional Skilling requests by state',
        'Show Regional Skilling request trend by month',
        'Show Regional Skilling requests by campaign',
        'Show in-progress Regional Skilling requests',
        'Show Regional Skilling requests by partner region',
        'Show Regional Skilling requests by skilling plan status',
        'Show my Regional Skilling requests',
        'Show Regional Skilling requests by request type',
        // Medium
        'Show Regional Skilling requests by state and priority',
        'Show Regional Skilling requests by campaign and sub-campaign',
        'Top 10 partners by Regional Skilling request count',
        'Average days to close Regional Skilling requests by priority',
        'Show Regional Skilling requests by partner type and country',
        'Show Regional Skilling requests by PDM',
        'Regional Skilling requests past due date grouped by priority',
        'Show monthly trend of skilling plan status',
        'Show Regional Skilling requests by delivery language',
    ],

    // ── SGS (Strategic Growth Shaping) ─────────────────────────────────
    [TENANT_IDS.SGS]: [
        // Basic
        'Show SGS actions by state',
        'Show SGS action trend by month',
        'Show SGS actions by SGS area',
        'Show in-progress SGS actions',
        'Top 10 SGS account owners by action count',
        'Show SGS actions by SGS segment',
        'Show my SGS actions',
        'Show SGS actions by primary growth shaping contact',
        // Medium
        'Show SGS actions by state and SGS segment',
        'Top 10 SGS accounts by action count',
        'Average days to close SGS actions by priority',
        'Show SGS actions by subsidiary',
        'Show SGS actions by growth lead',
        'SGS actions past due date grouped by priority',
        'Show monthly trend of created vs closed SGS actions',
        'Show SGS actions by AE contact and state',
        'Show SGS actions by ATS contact',
        'Show SGS actions where state is In Progress grouped by SGS area',
    ],

    // ── TechROB ────────────────────────────────────────────────────────
    [TENANT_IDS.TECH_ROB]: [
        // Basic
        'What percentage of milestones are blocked vs on track in tech rob?',
        'Show blocked actions by blocker type with average days blocked',
        'Which assigned tech rob teams have the longest average resolution time?',
        'Show P0/P1 actions by tech rob assigned team with days open — who needs help',
        'Month-over-month blocked milestone trend — is the tech blocker count improving?',
        'Which milestone workloads have the most blocked items this quarter?',
        'Show my Tech RoB actions',
        'Actions by milestone workload where customer is committed',
        'tech rob Actions where state is In Progress and due date is past',
        'top 10 accounts with blocked milestones',
        'Show Tech RoB actions by state',
        'Show Tech RoB action trend by month',
        'Show blocked milestones',
        'Show actions by blocker type',
        'Show actions by assigned team',
        'Show customer commitment breakdown — Required vs Committed vs Uncommitted',
        'Average days to close Tech RoB actions',
        'Show actions by milestone workload',
        'Which in-progress actions have been open the longest?',
        // Medium
        'Show blocked actions by blocker type and priority',
        'Actions by milestone workload where customer commitment is Required',
        'Show action count by assigned team and state',
        'Top 10 accounts with blocked milestones',
        'Show average days open for in-progress actions by assigned team',
        'Actions by sales play and opportunity stage',
        'Show actions created this quarter grouped by priority',
        'Which managing owners have the most blocked actions?',
        'Show trend of blocked vs on-track milestones by month',
        'Actions where state is In Progress and due date is past',
    ],
};

/**
 * All available sample prompts for the copilot (generic fallback)
 * Uses UATGeneral/ALL_REQUESTS prompts as the default pool
 */
export const allSamplePrompts: string[] = tenantAllPrompts[TENANT_IDS.ALL_REQUESTS];

/**
 * First 5 prompts to show in the bot welcome message suggestions (generic fallback)
 */
export const botSuggestionPrompts: string[] = tenantBotSuggestions[TENANT_IDS.ALL_REQUESTS];

/**
 * First 6 prompts to show in the prompts modal/suggestions (generic fallback)
 */
export const promptsSuggestionPrompts: string[] = tenantPromptsSuggestions[TENANT_IDS.ALL_REQUESTS];

/**
 * FAQ-only prompts for tenants that do not support Natural Language Queries (NL2SQL)
 * These tenants have warningText set (e.g., '[Tenant] natural language query support is coming soon.')
 * Only questions specifically covered in the UAT FAQ are available for these tenants
 */
export const faqOnlyPrompts: string[] = [
    // UAT Basics (FAQ #1, #2, #3)
    'What is Unified Action Tracker (UAT)?',
    'Why is UAT important?',
    'Has UAT been rebranded?',

    // Core Capabilities & Features (FAQ #4, #7, #20)
    'What are the core capabilities of UAT?',
    'What recent enhancements have been made to UAT?',
    'What AI-powered features are available in Action 360?',

    // Users & Impact (FAQ #5, #6)
    'Who uses UAT?',
    'How does UAT impact the business?',

    // Integration & Resources (FAQ #8, #9)
    'How does UAT integrate with other tools?',
    'Where can I find more resources about UAT?',

    // Creating Actions (FAQ #10, #16)
    'How do I submit a new action in UAT?',
    'How do I submit a Field Request?',

    // Request Types (FAQ #11, #12, #17, #18)
    'What is a Field Request?',
    'When should I use a Field Request?',
    'What is a General Request?',
    'When should I use a General Request?',

    // Assignments & Escalations (FAQ #13, #14, #15)
    "Who is the 'Assigned To' person?",
    "What does 'Assign to Corp' do?",
    "What should I enter in 'What help is needed'?",

    // AI Features (FAQ #21, #22, #23, #24, #25)
    'How does AI prevent duplicates in UAT?',
    'What is Copilot Discussion Highlights?',
    'What is Copilot Customer Insights?',
    'What tasks can the Copilot Chatbot handle in UAT?',
    'What measurable impact has AI delivered?',

    // Action 360 Improvements (FAQ #26, #34, #35, #36, #37, #38, #39, #40)
    'What improvements does Action 360 offer?',
    'How does Action 360 help users avoid duplicate submissions?',
    'What AI-driven features are available in Action 360?',
    'How does Action 360 improve triage efficiency?',
    'What performance improvements have been made in Action 360?',
    'How does Action 360 support compliance and security?',
    'What are Copilot Insights and Discussion Summaries in Action 360?',

    // AMM Request (FAQ #41)
    'How can I create a new AMM request?',
];

/**
 * FAQ-only bot suggestions for tenants without NL2SQL support
 * First 5 prompts to show in the bot welcome message
 */
export const faqBotSuggestionPrompts: string[] = [
    'What is Unified Action Tracker (UAT)?',
    'How do I submit a new action in UAT?',
    'What AI-powered features are available in Action 360?',
    'What are the core capabilities of UAT?',
];

/**
 * FAQ-only prompts suggestions for tenants without NL2SQL support
 * Prompts to show in the prompts modal/suggestions
 */
export const faqPromptsSuggestionPrompts: string[] = [
    'What is a Field Request?',
    'How does Action 360 help users avoid duplicate submissions?',
    'What is Copilot Discussion Highlights?',
    'What improvements does Action 360 offer?',
    'How does UAT integrate with other tools?',
    'How can I create a new AMM request?',
];

/**
 * Get tenant-specific bot suggestions, falling back to generic prompts
 */
export const getTenantBotSuggestions = (tenantId: string | null): string[] => {
    if (!tenantId) return tenantBotSuggestions[TENANT_IDS.ALL_REQUESTS] || botSuggestionPrompts;
    const prompts = tenantBotSuggestions[tenantId as TenantId];
    if (prompts && prompts.length > 0) {
        return prompts;
    }
    return botSuggestionPrompts;
};

/**
 * Get tenant-specific prompts suggestions, falling back to generic prompts
 */
export const getTenantPromptsSuggestions = (tenantId: string | null): string[] => {
    if (!tenantId) return tenantPromptsSuggestions[TENANT_IDS.ALL_REQUESTS] || promptsSuggestionPrompts;
    const prompts = tenantPromptsSuggestions[tenantId as TenantId];
    if (prompts && prompts.length > 0) {
        return prompts;
    }
    return promptsSuggestionPrompts;
};

/**
 * Get the full prompt pool for a tenant (basic + medium) for follow-up rotation
 * Falls back to UATGeneral (ALL_REQUESTS) prompts
 */
export const getTenantAllPrompts = (tenantId: string | null): string[] => {
    if (!tenantId) return tenantAllPrompts[TENANT_IDS.ALL_REQUESTS] || allSamplePrompts;
    const prompts = tenantAllPrompts[tenantId as TenantId];
    if (prompts && prompts.length > 0) {
        return prompts;
    }
    return allSamplePrompts;
};

/**
 * Convert a flat string[] of prompts into the PromptSection[] format
 * required by cst-web-chat's promptsSuggestionsV2 prop.
 *
 * This bypasses the library's internal singleton Redux store, which doesn't
 * reliably update promptsLibrarySuggestions on component remount.
 */
export const toPromptSections = (
    prompts: string[],
    sectionName = 'Suggested Prompts',
): PromptSection[] => {
    return [
        {
            sectionName,
            suggestions: prompts.map((prompt, index): PromptSuggestion => ({
                isDefault: true,
                pageUrl: null,
                feature: null,
                suggestion: prompt,
                category: 'Learn',
                orderkey: index + 1,
                type: 'Content',
                role: 'User',
                id: index + 1,
            })),
        },
    ];
};

/**
 * Get tenant-specific prompts in the PromptSection[] format for promptsSuggestionsV2.
 * Falls back to FAQ prompts for non-NL2SQL tenants.
 */
export const getTenantPromptSections = (
    tenantId: string | null,
    supportsNL2SQL: boolean,
): PromptSection[] => {
    const prompts = supportsNL2SQL
        ? getTenantPromptsSuggestions(tenantId)
        : faqPromptsSuggestionPrompts;
    return toPromptSections(prompts);
};

// Generated by GitHub Copilot | Updated on: 2026-04-01 | 600 lines modified | Replaced mock suggestions with full NL2SQL test prompts organized by tenant; added tenantAllPrompts pool and getTenantAllPrompts helper
// Generated by GitHub Copilot | Updated on: 2026-04-01 | 45 lines modified | Added toPromptSections and getTenantPromptSections helpers to bypass cst-web-chat singleton Redux store bug
// Generated by GitHub Copilot | Updated on: 2026-04-02 | 8 lines modified | Added PromptSection/PromptSuggestion type imports and updated function return types
// Generated by GitHub Copilot | Updated on: 2026-09-15 | 45 lines added | Added MARKETPLACE tenant to TENANT_IDS, bot suggestions, prompts gallery, and all-prompts pools
// Generated by GitHub Copilot | Updated on: 2026-09-15 | 42 lines added | Added MSecR tenant sample prompt pools
